import { BRAND, HEADER_CSS, esc, page, traceLine } from './base.js'

/**
 * ใบรับรองแพทย์ / Dental Certificate — HTML replacement for the Google Docs
 * template.
 *
 * Input is EXACTLY what buildMedicalCertPayload() in DocumentGenerator.jsx
 * already produces for the Apps Script renderer; this file only lays it out.
 * `lang: 'en'` reads the *EN fields of the same payload (medical_cert_en).
 *
 * Unlike the receipt this KEEPS a signature line: a certificate is the
 * dentist's own attestation and is signed by hand before it is handed over.
 *
 * Diagnosis / treatment / opinion are free text typed in the dialog — their
 * line breaks are kept (pre-wrap), never interpreted as markup.
 */

const L = {
  th: {
    title: 'ใบรับรองแพทย์', sub: 'Dental Certificate',
    ref: 'เลขที่อ้างอิง', hn: 'HN', date: 'วันที่ตรวจ',
    patient: 'ข้อมูลผู้ป่วย', age: 'อายุ', years: 'ปี', doctor: 'ทันตแพทย์ผู้รักษา', lic: 'ท.',
    issuer: 'สถานพยาบาล', clinicName: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา', branchLic: 'ใบอนุญาตประกอบกิจการสถานพยาบาล', sso: 'รหัสสถานพยาบาล',
    diagnosis: 'ผลการตรวจ / การวินิจฉัย', treatment: 'ให้การรักษาโดย', opinion: 'ความเห็นของทันตแพทย์ หรือแพทย์ผู้รักษา',
    role: 'ทันตแพทย์ผู้รักษา',
    clinic: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา',
  },
  en: {
    title: 'Dental Certificate', sub: 'ใบรับรองแพทย์',
    ref: 'Reference no.', hn: 'HN', date: 'Date of examination',
    patient: 'Patient', age: 'Age', years: 'years', doctor: 'Attending dentist', lic: 'License',
    issuer: 'Healthcare facility', clinicName: 'Smile Seasons Dental Clinic · ', branchLic: 'Healthcare facility license', sso: 'Facility code',
    diagnosis: 'Examination / Diagnosis', treatment: 'Treatment provided', opinion: "Dentist's opinion",
    role: 'Attending dentist',
    clinic: 'Smile Seasons Dental Clinic · ',
  },
}

export function renderMedicalCert(d, { lang = 'th', assets = {} } = {}) {
  const t = L[lang] || L.th
  const en = lang === 'en'
  const pick = (th, enKey) => (en && d[enKey] != null && d[enKey] !== '' ? d[enKey] : d[th])

  const branch = pick('branch_name', 'branch_nameEN')
  const doctorTh = `${d.doctor_prefix || ''}${d.doctor_fullname || ''}`.trim()
  const doctor = en ? (d.doctor_fullnameEN || doctorTh) : doctorTh
  const patient = en
    ? (d.fullnameEN || d.patient_fullname || d.fullname)
    : `${d.patient_prefix || ''}${d.patient_fullname || d.fullname || ''}`
  const examDate = en ? (d.date || d.date_thai) : (d.date_thai || d.date)
  const lic = d.doctor_license ? `${t.lic} ${esc(d.doctor_license)}` : ''

  // The attestation paragraph — the same wording as the Google Docs template.
  const statement = en
    ? `I, <b>${esc(doctor)}</b>, a dentist registered and licensed to practise dentistry${d.doctor_license ? ` (license no. <b class="num">${esc(d.doctor_license)}</b>)` : ''}, examined and treated the oral cavity of <b>${esc(patient)}</b>${d.patient_age ? `, aged <b class="num">${esc(d.patient_age)}</b> years` : ''}, patient ID (HN) <b class="num">${esc(d.HN)}</b>, on <b>${esc(examDate)}</b>.`
    : `ข้าพเจ้า <b>${esc(doctorTh)}</b> เป็นทันตแพทย์ซึ่งได้ขึ้นทะเบียนรับอนุญาตให้ประกอบวิชาชีพเวชกรรมสาขาทันตกรรม${d.doctor_license ? ` ใบอนุญาตประกอบวิชาชีพทันตกรรมหมายเลข <b class="num">ท. ${esc(d.doctor_license)}</b>` : ''} ได้ทำการตรวจและรักษาช่องปากของ <b>${esc(patient)}</b>${d.patient_age ? ` อายุ <b class="num">${esc(d.patient_age)}</b> ปี` : ''} รหัสผู้ป่วย (HN) <b class="num">${esc(d.HN)}</b> เมื่อวันที่ <b>${esc(examDate)}</b>`

  const block = (label, text) => `
  <section class="block">
    <div class="block-label">${label}</div>
    <div class="block-body">${String(text ?? '').trim() ? esc(String(text).trim()) : '<span class="muted">-</span>'}</div>
  </section>`

  const body = `
  <header class="top">
    <div>
      <h1>${t.title}</h1>
      <div class="sub">${t.sub}</div>
    </div>
    ${assets.logo ? `<img class="logo" src="${esc(assets.logo)}" alt="Smile Seasons Dental Clinic">` : ''}
  </header>

  <section class="meta">
    <div><span>${t.ref}</span><b class="num">${esc(d.ref_num || '-')}</b></div>
    <div><span>${t.date}</span><b>${esc(examDate || '-')}</b></div>
  </section>

  <section class="parties">
    <div class="party">
      <div class="label">${t.patient}</div>
      <div class="name">${esc(patient)}</div>
      <div class="kv"><span>${t.hn}</span>${esc(d.HN)}</div>
      ${d.patient_age ? `<div class="kv"><span>${t.age}</span>${esc(d.patient_age)} ${t.years}</div>` : ''}
      <div class="kv"><span>${t.doctor}</span>${esc(doctor)}${lic ? ` <span class="muted">(${lic})</span>` : ''}</div>
    </div>
    <div class="party">
      <div class="label">${t.issuer}</div>
      <div class="name">${esc(t.clinicName)}${esc(branch || '')}</div>
      ${d.branch_license_number ? `<div class="kv"><span>${t.branchLic}</span><span class="num">${esc(d.branch_license_number)}</span></div>` : ''}
      ${d.branch_ssonumber ? `<div class="kv"><span>${t.sso}</span><span class="num">${esc(d.branch_ssonumber)}</span></div>` : ''}
    </div>
  </section>

  <p class="statement">${statement}</p>

  ${block(t.diagnosis, d.diagnosis)}
  ${block(t.treatment, d.treatment)}
  ${block(t.opinion, d.opinion)}

  <section class="sign">
    <div class="sign-box">
      <div class="sign-line"></div>
      <b>(${esc(doctor)})</b>
      <span>${t.role}${lic ? ` · ${lic}` : ''}</span>
    </div>
  </section>`

  return page({
    title: `${t.title} ${d.ref_num || d.HN || ''}`.trim(),
    lang,
    assets,
    footer: `${t.clinic}${branch || ''}`,
    meta: traceLine(d, lang),
    body,
    css: CSS,
  })
}

const CSS = `${HEADER_CSS}
.statement { margin: 0 0 6mm; font-size: 10.5pt; line-height: 1.75; text-indent: 8mm; }
.statement b { font-weight: 600; }

.block { margin-bottom: 5mm; break-inside: avoid; }
.block-label { padding: 1.8mm 3mm; background: ${BRAND.blueSoft}; border-left: 2px solid ${BRAND.blue}; font-size: 9pt; font-weight: 600; color: ${BRAND.blue}; }
.block-body { padding: 2.4mm 3mm 0; min-height: 12mm; white-space: pre-wrap; overflow-wrap: anywhere; }

.sign { display: flex; justify-content: flex-end; margin-top: 14mm; break-inside: avoid; }
.sign-box { width: 72mm; display: flex; flex-direction: column; align-items: center; text-align: center; font-size: 9.5pt; }
.sign-line { width: 100%; height: 14mm; border-bottom: 1px dotted #94a3b8; margin-bottom: 2mm; }
.sign-box b { font-weight: 600; font-size: 10.5pt; }
.sign-box span { color: ${BRAND.muted}; }
`
